import { useTranslation } from '@hbs-us/ui';
import { getRouteApi } from '@tanstack/react-router';
import { RouteSkeleton } from '../_ops/RouteSkeleton.js';
import { TimesheetsView } from './TimesheetsView.js';
import { useTimesheetsQuery } from './useTimesheetsQuery.js';

/**
 * The `/timesheets` route component (promoted from a ComingSoon stub this plan — the heaviest screen).
 * Reads the typed `week` search param, fires the READ-ONLY coarse week-grid query, and renders the
 * faithful `TimesheetsView` on success. Loading → a page-shaped `RouteSkeleton`; error → announced error state
 * with a retry that refetches (the estimates/invoices 3-state contract, verbatim tokens).
 *
 * The server computes the enriched grid + totals + edit-requests + active clocks; this component never
 * re-aggregates and never sends a tenant id (SEC-11). It never recomputes the lunch/overtime math —
 * that stays server-side (D-03 / T-22-payroll).
 */

const routeApi = getRouteApi('/timesheets');

export function TimesheetsRoute() {
  const { t } = useTranslation();
  const search = routeApi.useSearch();
  const query = useTimesheetsQuery(search);

  if (query.isPending) {
    return (
      <RouteSkeleton
        label={t('timesheets:title')}
        rows={8}
        columns={5}
        filters={3}
        action={false}
      />
    );
  }

  if (query.isError) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('timesheets:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('timesheets:error.body')}</p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm ring-offset-background transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t('timesheets:error.retry')}
        </button>
      </section>
    );
  }

  // While the next week loads, the previous week's grid is still on screen (keepPreviousData), which
  // keeps the week-nav buttons from vanishing under a full-page spinner. Say that it is updating, or
  // the opposite fault appears: the click seems to do nothing because the old week is still shown.
  return (
    <div
      aria-busy={query.isPlaceholderData}
      className={query.isPlaceholderData ? 'opacity-60 transition-opacity' : undefined}
    >
      <TimesheetsView data={query.data} search={search} />
    </div>
  );
}
